/** Aba "Movimentações": o ledger, do mais recente para o mais antigo. */
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { TireMovementRowDto, TireMovementTypeDto } from "@bi/types";
import { api } from "../../../lib/api";
import { EmptyState } from "../../../components/EmptyState";
import { IconSync } from "../../../components/icons";
import { fmtDateTime, fmtKm, MOVEMENT_TYPE_LABEL, OriginBadge } from "../tire-ui";

export function TireMovementsTab() {
  const [params, setParams] = useSearchParams();
  const type = params.get("tipo") ?? "";
  const plate = params.get("placa") ?? "";
  const from = params.get("de") ?? "";
  const to = params.get("ate") ?? "";
  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p, { replace: true });
  };

  const q = useQuery({
    queryKey: ["tires", "movements", type, plate, from, to],
    queryFn: () => {
      const p = new URLSearchParams({ limit: "200" });
      if (type) p.set("type", type);
      if (plate) p.set("plate", plate);
      if (from) p.set("from", `${from}T00:00:00`);
      if (to) p.set("to", `${to}T23:59:59`);
      return api.get<{ total: number; rows: TireMovementRowDto[] }>(`/fleet/tire-movements?${p}`);
    },
  });

  return (
    <div className="space-y-4">
      <div className="card p-3 flex flex-wrap gap-2 items-center">
        <select className="input w-52" value={type} onChange={(e) => set("tipo", e.target.value)}>
          <option value="">Todo tipo</option>
          {(Object.keys(MOVEMENT_TYPE_LABEL) as TireMovementTypeDto[]).map((t) => <option key={t} value={t}>{MOVEMENT_TYPE_LABEL[t]}</option>)}
        </select>
        <input className="input w-32 uppercase font-mono" placeholder="Veículo" defaultValue={plate}
          onBlur={(e) => set("placa", e.target.value.trim().toUpperCase())}
          onKeyDown={(e) => { if (e.key === "Enter") set("placa", (e.target as HTMLInputElement).value.trim().toUpperCase()); }} />
        <label className="text-xs text-gray-500 inline-flex items-center gap-1">De <input type="date" className="input" value={from} onChange={(e) => set("de", e.target.value)} /></label>
        <label className="text-xs text-gray-500 inline-flex items-center gap-1">Até <input type="date" className="input" value={to} onChange={(e) => set("ate", e.target.value)} /></label>
      </div>
      <div className="card overflow-x-auto">
        {q.isLoading ? <p className="p-6 text-sm text-gray-500">Carregando…</p>
          : (q.data?.rows ?? []).length === 0 ? (
            <EmptyState icon={<IconSync className="w-5 h-5" />} title="Nenhuma movimentação registrada"
              description="Instalações, retiradas, rodízios e transferências feitos no SIAG aparecem aqui." />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-surface-muted text-xs text-gray-500 uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Data</th>
                  <th className="text-left px-4 py-2">Pneu</th>
                  <th className="text-left px-4 py-2">Movimentação</th>
                  <th className="text-left px-4 py-2">Origem</th>
                  <th className="text-left px-4 py-2">Destino</th>
                  <th className="text-right px-4 py-2">KM</th>
                  <th className="text-left px-4 py-2 hidden lg:table-cell">Motivo</th>
                  <th className="text-left px-4 py-2 hidden md:table-cell">Por</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {q.data!.rows.map((m) => (
                  <tr key={m.id}>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">{fmtDateTime(m.occurredAt)}</td>
                    <td className="px-4 py-3 font-mono"><Link className="hover:underline" to={`/frota/pneus/${m.tireId}`}>{m.tireCode}</Link></td>
                    <td className="px-4 py-3">
                      <span className="text-gray-900">{m.typeLabel}</span>{" "}
                      {m.origin !== "siag" && <OriginBadge origin="finatto" />}
                      {m.workOrderId && <Link className="ml-1 text-xs text-tm-700 hover:underline" to={`/frota/manutencao/ordens/${m.workOrderId}`}>O.S.</Link>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{m.sourcePlate ? `${m.sourcePlate}${m.sourcePosition ? ` · ${m.sourcePosition}` : ""}` : "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{m.destinationPlate ? `${m.destinationPlate}${m.destinationPosition ? ` · ${m.destinationPosition}` : ""}` : "—"}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">{fmtKm(m.odometerKm)}</td>
                    <td className="px-4 py-3 text-gray-600 hidden lg:table-cell max-w-[16rem] truncate" title={m.reason ?? ""}>{m.reason ?? "—"}</td>
                    <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{m.by ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
      {q.data && q.data.total > q.data.rows.length && (
        <p className="text-xs text-gray-500">Mostrando {q.data.rows.length} de {q.data.total}. Refine pelos filtros.</p>
      )}
    </div>
  );
}
